import { useLookbookContext } from '@app/components/lookbook/context/LookbookContext';
import { useLookbookProductItemContext } from '@app/components/products/context/LookbookProductItemContext';
import { lookbookLocationToTracking } from '@app/logic/lookbook';
import { GAProductListItemId, GAProductListItemName, mapProductToGAItem, useAmplitude, useGTM } from '@app/utils/analytics';
import { useGallery } from '@app/utils/hooks/useGallery';
import { Product } from '@typings/@centra';

import { LookbookProductBadges } from '../../LookbookProductBadges';

import { GallerySlide } from './GallerySlide';

interface Props {
  isCompact?: boolean;
  overlapHeight?: number;
  parentWidth: number;
  isDisabled?: boolean;
  shouldSlideUpOnHover?: boolean;
  className?: string;
}
export const LookbookGallery = ({
  isCompact = false,
  overlapHeight = 0,
  parentWidth,
  isDisabled = false,
  className,
  shouldSlideUpOnHover = false,
}: Props) => {
  const {
    activeSlideIndex,
    gallerySlides,
    setActiveSlideIndex,
    productsGridSize,
    hasHoverOrFocus,
    productPathName,
    isReadOnly,
    isActiveVariantWishlisted,
    toggleProductInWishlist,
  } = useLookbookProductItemContext();
  const { locationContext } = useLookbookContext();
  const { ref } = useGallery({
    activeIndex: activeSlideIndex,
    onChange: setActiveSlideIndex,
    orientation: 'horizontal',
  });
  const { trackEvent } = useAmplitude();
  const { trackGTMEvent } = useGTM();

  const handleWishlistToggle = (variant: Product.Standard) => () => {
    if (!isActiveVariantWishlisted) {
      trackEvent({ name: `${lookbookLocationToTracking[locationContext]}.product.addToWishlist` });
      trackGTMEvent('add_to_wishlist', {
        ecommerce: {
          items: [
            mapProductToGAItem(variant, { item_list_id: GAProductListItemId.Lookbook, item_list_name: GAProductListItemName.Lookbook }),
          ],
        },
      });
    } else {
      trackEvent({ name: `${lookbookLocationToTracking[locationContext]}.product.removeFromWishlist` });
    }
    toggleProductInWishlist();
  };

  return (
    <>
      <div
        ref={ref}
        className="flex snap-x snap-mandatory gap-px overflow-x-hidden overscroll-x-none no-scrollbar touch:overflow-x-auto"
        {...(isDisabled ? { inert: '' } : undefined)}
      >
        {gallerySlides.map((slide, index) => (
          <GallerySlide
            key={slide.id}
            isActive={activeSlideIndex === index}
            image={slide.image}
            badges={
              <LookbookProductBadges
                badgeSize={isCompact ? 'small' : 'medium'}
                spacing={productsGridSize === 'large' ? 'medium' : 'small'}
                variant={slide.variant}
                overlapHeight={overlapHeight}
                parentWidth={parentWidth}
              />
            }
            hasHoverOrFocus={hasHoverOrFocus}
            productPathName={productPathName}
            productsGridSize={productsGridSize}
            handleFavoriteToggle={!isReadOnly ? handleWishlistToggle(slide.variant) : undefined}
            isFavorite={isActiveVariantWishlisted}
            imageClassName={className}
            shouldSlideUpOnHover={shouldSlideUpOnHover}
          />
        ))}
      </div>
    </>
  );
};
